import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { LevelResult } from '../types';

type TelemetryProperties = Record<string, string | number | boolean>;
interface PendingEvent {
  name: string;
  properties?: TelemetryProperties;
}

let client: ApplicationInsights | undefined;
let initializationAttempted = false;
let telemetryConfigured = false;
let telemetryAllowed = false;
let loadGeneration = 0;
const pendingEvents: PendingEvent[] = [];

function sendEvent(name: string, properties?: TelemetryProperties): void {
  if (client) {
    client.trackEvent({ name }, properties);
  } else if (telemetryConfigured && pendingEvents.length < 25) {
    pendingEvents.push({ name, properties });
  }
}

/**
 * Initializes privacy-conscious browser telemetry when a connection string is
 * supplied at build time. With no string, every telemetry method is a no-op.
 *
 * The SDK is deliberately configured without cookies, browser storage,
 * request tracking, authenticated users, or arbitrary user-provided values.
 */
export function initTelemetry(): void {
  if (!telemetryAllowed || initializationAttempted) return;
  initializationAttempted = true;

  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim();
  if (!connectionString) return;
  telemetryConfigured = true;
  const generation = loadGeneration;

  void import('@microsoft/applicationinsights-web').then(({ ApplicationInsights }) => {
    if (!telemetryAllowed || generation !== loadGeneration) return;
    const applicationInsights = new ApplicationInsights({
      config: {
        connectionString,
        disableCookiesUsage: true,
        isStorageUseDisabled: true,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        disableCorrelationHeaders: true,
        enableAutoRouteTracking: false,
        enableUnhandledPromiseRejectionTracking: true
      }
    });
    applicationInsights.loadAppInsights();
    client = applicationInsights;
    for (const event of pendingEvents.splice(0)) {
      sendEvent(event.name, event.properties);
    }
  }).catch(() => {
    client = undefined;
    telemetryConfigured = false;
    pendingEvents.length = 0;
    console.warn('Telemetry could not be initialized. The game will continue without monitoring.');
  });
}

/** Applies and immediately enforces the visitor's optional analytics choice. */
export function setTelemetryConsent(allowed: boolean): void {
  telemetryAllowed = allowed;
  if (allowed) {
    initTelemetry();
    return;
  }

  loadGeneration++;
  client?.unload(false);
  client = undefined;
  initializationAttempted = false;
  telemetryConfigured = false;
  pendingEvents.length = 0;
}

export function trackAppLoaded(): void {
  sendEvent('app_loaded', {
    orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
    touch_capable: navigator.maxTouchPoints > 0
  });
}

export function trackAssetsLoaded(): void {
  sendEvent('assets_loaded');
}

export function trackAssetLoadFailure(assetKey: string, assetType: string): void {
  sendEvent('asset_load_failed', {
    asset_key: assetKey,
    asset_type: assetType
  });
}

export function trackLevelStarted(levelId: number): void {
  sendEvent('level_started', { level_id: levelId });
}

export function levelCompletedProperties(result: LevelResult): TelemetryProperties {
  return {
    level_id: result.levelId,
    score: result.score,
    stars: result.stars,
    best_combo: result.bestCombo,
    passed: result.stars > 0
  };
}

export function trackLevelCompleted(result: LevelResult): void {
  sendEvent('level_completed', levelCompletedProperties(result));
}
